import React, { useEffect, useMemo } from 'react';
import { MessageList } from '../message-list/message-list';
import { MessageInput } from '../message-input/message-input';
import { ConversationsModalGroup } from '../conversations-modal-group/conversations-modal-group';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  dispatch,
  getCurrentConversation,
  getWorkspaceId,
  updateCurrentConversation,
  useAppSelector,
  useLazyGetConversationsQuery,
} from '@zonofi/zono-money-store';
import { useGetConversationMessageList } from '../hooks/get-conversation-message-list';
import { useGetCommonConverationFunctions } from '../hooks/get-common-conversation-functions';
import { ConversationSummary } from '../conversation-summary/conversation-summary';
import './conversation-messages-screen-content.scss';
import { PullRefreshContainer } from '../../components';

export const ConversationMessageScreenContent: React.FC = () => {
  const {
    conversationId: conversationThreadId = '',
    customerId: pathCustomerId,
  } = useParams();
  const [searchParams] = useSearchParams();
  const queryCustomerId = searchParams.get('customerId');

  const customerId = useMemo(
    () => pathCustomerId || queryCustomerId || undefined,
    [pathCustomerId, queryCustomerId]
  );

  const currentConversation = useAppSelector(getCurrentConversation);
  const workspaceId = useAppSelector(getWorkspaceId);

  const [fetchConversations, conversationsResponse] =
    useLazyGetConversationsQuery();

  useEffect(() => {
    if (!conversationThreadId || !workspaceId) return;
    if (conversationThreadId === currentConversation?.conversationThreadId)
      return;

    fetchConversations({
      sellerWorkspaceId: workspaceId,
      conversationId: conversationThreadId,
      limit: 1,
    });
  }, [
    conversationThreadId,
    workspaceId,
    currentConversation?.conversationThreadId,
  ]);

  useEffect(() => {
    if (conversationsResponse.isSuccess && conversationsResponse.data?.data) {
      const conversation = Array.isArray(conversationsResponse.data.data[0])
        ? conversationsResponse.data.data[0][0]
        : conversationsResponse.data.data[0];

      dispatch(updateCurrentConversation(conversation));
    }
  }, [conversationsResponse]);

  const {
    messages,
    isLoading,
    handleSendMsg,
    oldestFirst,
    setOldestFirst,
    refetchMessages,
    channel,
  } = useGetConversationMessageList({
    conversationId: conversationThreadId,
    customerId,
    channelId: currentConversation?.channelId,
  });

  const {
    findStatusCategory,
    onActionPress,
    updateSelectedConversation,
    modalState,
    onCloseModal,
    findTagType,
  } = useGetCommonConverationFunctions();

  return (
    <div className="conversation-messages-screen-content">
      {currentConversation && (
        <ConversationSummary
          findStatusCategory={findStatusCategory}
          findTagType={findTagType}
          onAssignTeamMember={() =>
            onActionPress({
              action: 'assign',
              conversation: currentConversation,
            })
          }
          onAddTag={() =>
            onActionPress({
              action: 'tags',
              conversation: currentConversation,
            })
          }
          {...currentConversation}
        />
      )}

      <PullRefreshContainer onRefresh={refetchMessages}>
        <MessageList
          data={messages}
          isLoading={isLoading}
          oldestFirst={oldestFirst}
          setOldestFirst={setOldestFirst}
        />
      </PullRefreshContainer>

      <MessageInput
        channelId={channel?.id}
        conversationId={conversationThreadId}
        handleSendMsg={handleSendMsg}
      />

      <ConversationsModalGroup
        modalState={modalState}
        onCloseModal={onCloseModal}
        updateConversations={updateSelectedConversation}
        onActionPress={onActionPress}
        findTagType={findTagType}
      />
    </div>
  );
};
